const { matchingFeatures } = require('../lib/card-features');

/**
 * @param {{tactics: Record<string, boolean | number>}} card 
 * @returns {string[]} List of assigned tactics
 */
export function selectedTactics(card) {
	return Object.entries(card.tactics)
		.filter(([_, selected]) => {
			return selected;
		})
		.map(([tactic, _]) => tactic)
	;
}

/**
 * @param {string | null | undefined} a 
 * @param {string | null | undefined} b 
 * @returns {number} Percentage similarity (represented as a decimal)
 */
export function tokenSimilarity(a, b) {
	const aParts = new Set((a || '').split(/\s[—-]?\s?/));
	const bParts = new Set((b || '').split(/\s[—-]?\s?/));
	let matches = 0;
	for (const part of bParts) {
		if (aParts.has(part)) matches += 1;
	}
	const possibleMatches = aParts.size || 100;
	return possibleMatches === 0 ? 1.00 : matches / possibleMatches;
}

/**
 * 
 * @param {{manaCost: string}} card Card with `manaCost` property in "braces" format. E.g., "{W}{B}".
 * @returns Boolean map of colors from card cost.
 */
export function colors(card) {
	const result = {
		white: card.manaCost?.includes("W") || false,
		blue: card.manaCost?.includes("U") || false,
		black: card.manaCost?.includes("B") || false,
		red: card.manaCost?.includes("R") || false,
		green: card.manaCost?.includes("G") || false
	};
	result.colorless = !(
		result.white
		|| result.blue
		|| result.black
		|| result.red
		|| result.green
	);
	return result;
};

/**
 * 
 * @param {{manaCost: string}} card Card with `manaCost` property in "braces" format. E.g., "{W}{B}".
 * @returns {string} Comma-separate list of colors. E.g., "white, blue".
 */
export function colorString(card) {
	const colorArray = [];
	for (const [color, isPresent] of Object.entries(colors(card))) {
		if (isPresent) colorArray.push(color);
	}
	return colorArray.join(', ');
};

/**
 * 
 * @param {{manaCost: string}} cardA Card with `manaCost` property in "braces" format. E.g., "{W}{B}".
 * @param {{manaCost: string}} cardB Card to compare to with `manaCost` property in "braces" format. E.g., "{W}{B}".
 * @returns {number} Percentage similarity (represented as a decimal)
 */
export function colorSimilarity(cardA, cardB) {
	const a = colors(cardA);
	const b = colors(cardB);
	return (
		Number(a.white && b.white)
		+ Number(a.blue && b.blue)
		+ Number(a.black && b.black)
		+ Number(a.red && b.red)
		+ Number(a.green && b.green)
		+ Number(a.colorless && b.colorless)
	) / (
		Number(a.white || b.white)
		+ Number(a.blue || b.blue)
		+ Number(a.black || b.black)
		+ Number(a.red || b.red)
		+ Number(a.green || b.green)
		+ Number(a.colorless || b.colorless)
	);
};

export function findSimilarities(cards) {
	const weights = {
		textTokensScore: 0,
		featureScore: 0,
		colorScore: 0,
		typeScore: 0,
		cmcScore: 0
	};
	const MAX_COMPARISONS = 10_000;
	let comparisons = 0;
	let totalWeight = 0;
	for (let i = 0; i < cards.length && comparisons < MAX_COMPARISONS; i++) {
		for (let j = i + 1; j < cards.length && comparisons < MAX_COMPARISONS; j++) {
			const score = similarity(cards[i], cards[j]);
			if (!score) continue;
			for (const component of Object.keys(weights)) {
				comparisons++;
				totalWeight += score.subScores[component] || 0;
				weights[component] += score.subScores[component] || 0;
			}
		}
	}
	
	if (totalWeight == 0 || Object.values(weights).every(w => w === 0)) return {};

	for (const component of Object.keys(weights)) {
		weights[component] = weights[component] / totalWeight;
	}

	return weights;
}

export function getEffectiveWeights(providedWeights) {
	const weightOverrides = typeof providedWeights === 'object' ? providedWeights : {};
	const weights = {
		textTokensScore: 0.35,
		featureScore: 0.30,
		colorScore: 0.20,
		typeScore: 0.10,
		cmcScore: 0.05,
	};
	return {
		...weights,
		...weightOverrides
	};
}

export function weightedSum(weights, values) {
	let sum = 0;
	for (const k of Object.keys(weights)) {
		sum += (values[k] || 0) * weights[k];
	}
	return sum;
}

// TODO: Refactor so that `weightOverrides` are just a requied `weights`
// parameter so they're not re-determined for every card.
export function similarity(card, candidateCard, weightOverrides) {
	if (!(typeof card === 'object' && typeof candidateCard === 'object')) {
		return null
	};
	const features = matchingFeatures(card);
	const matches = matchingFeatures(candidateCard, features);
	const weights = getEffectiveWeights(weightOverrides);

	const subScores = {
		featureScore: features.length === 0
			? 1.00
			: matches.length / features.length,
		textTokensScore: tokenSimilarity(card.text, candidateCard.text),
		typeScore: tokenSimilarity(card.type, candidateCard.type),
		cmcScore: 1 - Math.abs(card.cmc - candidateCard.cmc)/(
			(card.cmc + candidateCard.cmc) || 1
		),
		colorScore: colorSimilarity(card, candidateCard),
	};

	const score = weightedSum(weights, subScores);

	return {
		features: matches,
		card: candidateCard,
		weights,
		score,
		subScores
	};
};


export function quickSimilarity(card, candidateCard) {
	if (!(typeof card === 'object' && typeof candidateCard === 'object')) {
		return null
	};
	const typeScore = tokenSimilarity(card.type, candidateCard.type);
	const cmcScore = 1 - Math.abs(card.cmc - candidateCard.cmc)/(
		(card.cmc + candidateCard.cmc) || 1
	);
	const colorScore = colorSimilarity(card, candidateCard);

	const score = (0
		+ .50 * typeScore
		+ .25 * colorScore
		+ .25 * cmcScore
	);

	return {
		card: candidateCard,
		colorScore,
		typeScore,
		cmcScore,
		score
	};
};


/**
 * Compare `a` and `b` by their `score` properties for *descending* sort order.
 * 
 * @param {{score: number}} a 
 * @param {{score: number}} b 
 * @returns {-1 | 0 | 1}
 */
export function byScore(a, b) {
	// note: this looks reversed b/c sort is descending.
	// this is intentional.
	if (a.score > b.score) return -1;
	if (a.score < b.score) return 1;
	return 0;
};
